import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { validateCoupon, CouponValidation } from '../services/couponService';
import { ArrowLeft, Lock, Info, Loader } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

const PageContainer = styled.div`
  padding-top: var(--header-h);
  min-height: 100vh;
  background: #f5f5f5;
  width: 100%;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px 60px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #333;
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 30px;
  transition: color 0.3s ease;

  &:hover {
    color: #666;
  }
`;

const CheckoutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 40px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const RightPanel = styled.div`
  position: sticky;
  top: 140px;
  height: fit-content;
`;

const Section = styled.div`
  background: white;
  border-radius: 8px;
  padding: 30px;
  border: 1px solid #ddd;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h2 {
    font-size: 1.5rem;
    color: #333;
    font-weight: 600;
    margin: 0;
  }
  
  a {
    color: #333;
    text-decoration: none;
    font-size: 0.9rem;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    margin-bottom: 8px;
    color: #333;
    font-size: 0.9rem;
    font-weight: 500;
  }
  
  input, select {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 1rem;
    transition: border-color 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: #333;
    }
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #333;
  
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const ShippingMethod = styled.div`
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-bottom: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .method {
    display: flex;
    align-items: center;
    gap: 10px;
    
    input[type="radio"] {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }
  }
  
  .price {
    font-weight: 600;
    color: #333;
  }
`;

const PaymentMethod = styled.div`
  padding: 20px;
  border: 2px solid #333;
  border-radius: 8px;
  margin-bottom: 20px;
  background: #f9f9f9;
  
  .method-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 15px;
    
    input[type="radio"] {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }
    
    .logos {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-left: auto;
      
      img {
        height: 24px;
      }
      
      span {
        font-size: 0.9rem;
        color: #666;
      }
    }
  }
  
  .payment-info {
    margin-top: 15px;
    padding: 15px;
    background: white;
    border-radius: 6px;
    font-size: 0.9rem;
    color: #666;
    line-height: 1.6;
  }
`;

const SecurityText = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 20px;
  
  svg {
    color: #28a745;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  
  label {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    
    input[type="radio"] {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }
  }
`;

const PayButton = styled.button`
  width: 100%;
  background: #000;
  color: white;
  border: none;
  padding: 15px;
  border-radius: 6px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease;
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  
  &:hover:not(:disabled) {
    background: #333;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 12px;
  border-radius: 6px;
  margin-top: 15px;
  font-size: 0.9rem;
  border: 1px solid #fcc;
`;

const ThreeDSContainer = styled.div`
  margin-top: 20px;
  iframe {
    width: 100%;
    min-height: 400px;
    border: 1px solid #ddd;
    border-radius: 6px;
  }
`;

const OrderSummary = styled.div`
  background: white;
  border-radius: 8px;
  padding: 30px;
  border: 1px solid #ddd;
`;

const ProductItem = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
  
  img {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 6px;
  }
  
  .details {
    flex: 1;
    
    h4 {
      font-size: 0.9rem;
      color: #333;
      margin-bottom: 5px;
      font-weight: 500;
    }
    
    p {
      font-size: 0.85rem;
      color: #666;
      margin: 0;
    }
    
    .price {
      font-size: 1rem;
      font-weight: 600;
      color: #333;
      margin-top: 5px;
    }
  }
`;

const DiscountCode = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  
  input {
    flex: 1;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.9rem;
  }
  
  button {
    padding: 10px 20px;
    background: #333;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    
    &:hover {
      background: #555;
    }
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  font-size: 0.95rem;
  color: #333;
  
  &.total {
    font-size: 1.2rem;
    font-weight: 700;
    padding-top: 15px;
    border-top: 2px solid #333;
    margin-top: 15px;
  }
`;

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { cartItems, getTotalPrice } = useCart();
  const auth = getAuth();
  const db = getFirestore();
  const user = auth.currentUser;
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [threeDSHtml, setThreeDSHtml] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    surname: '',
    address: '',
    apartment: '',
    postalCode: '',
    city: '',
    phone: '',
    country: 'Türkiye',
    identityNumber: ''
  });
  const [billingData, setBillingData] = useState({
    name: '',
    surname: '',
    address: '',
    apartment: '',
    postalCode: '',
    city: '',
    phone: '',
    country: 'Türkiye'
  });
  const [cardData, setCardData] = useState({
    cardHolderName: '',
    cardNumber: '',
    expireMonth: '',
    expireYear: '',
    cvc: ''
  });

  const subtotal = getTotalPrice();

  // --- Kupon ---
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<CouponValidation | null>(null);
  const [couponError, setCouponError] = useState('');
  const [checkingCoupon, setCheckingCoupon] = useState(false);

  const handleApplyCoupon = async () => {
    setCouponError('');

    if (!couponInput.trim()) {
      setCouponError('Lütfen bir kupon kodu girin.');
      return;
    }

    setCheckingCoupon(true);
    const result = await validateCoupon(couponInput, subtotal);
    setCheckingCoupon(false);

    if (!result.valid) {
      setAppliedCoupon(null);
      setCouponError(result.error || 'Kupon geçersiz.');
      return;
    }

    setAppliedCoupon(result);
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponError('');
  };

  const discount = appliedCoupon?.discount || 0;
  const baseShipping = subtotal > 10000 ? 0 : 500;
  const shipping = appliedCoupon?.freeShipping ? 0 : baseShipping;
  const total = Math.max(0, subtotal - discount + shipping);
  const threeDSFormRef = useRef<HTMLDivElement>(null);

  // Kullanıcı verilerini çek ve formu doldur
  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        try {
          console.log('🔍 Fetching user data for:', user.uid);
          
          // Firestore'dan detaylı bilgileri çek
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            const data = userDocSnap.data();
            console.log('✅ User data from Firestore:', data);
            
            // Auth'dan gelen temel bilgiler
            const newFormData = {
              email: user.email || '',
              name: data.firstName || '',
              surname: data.lastName || '',
              address: '',
              apartment: '',
              postalCode: '',
              city: '',
              phone: data.phoneNumber || data.phone || '',
              country: 'Türkiye',
              identityNumber: ''
            };
            
            // Profilde adresler dizi ise, ilkini varsayılan olarak alıyoruz
            if (data.addresses && data.addresses.length > 0) {
              console.log('📍 Found address:', data.addresses[0]);
              newFormData.address = data.addresses[0];
            } else {
              console.log('⚠️ No addresses found in user data');
            }
            
            console.log('📝 Setting form data:', newFormData);
            // Formu güncelle
            setFormData(newFormData);
          } else {
            console.log('⚠️ User document does not exist in Firestore');
            // Sadece email'i set et
            setFormData(prev => ({
              ...prev,
              email: user.email || ''
            }));
          }
        } catch (error) {
          console.error('❌ Kullanıcı verileri çekilirken hata:', error);
        }
      }
    };

    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // 3D Secure HTML render edildikten sonra script'lerin çalışması için
  useEffect(() => {
    if (threeDSHtml && threeDSFormRef.current) {
      console.log('✅ 3D Secure HTML render edildi, script\'lerin çalışması bekleniyor...');
      
      // HTML içindeki script'lerin çalışması için kısa bir bekleme
      // Genellikle iyzico'nun HTML'i içindeki script otomatik formu submit eder
      setTimeout(() => {
        // Eğer script çalışmadıysa, formu manuel submit etmeyi dene
        const form = threeDSFormRef.current?.querySelector('form[name="returnform"]') as HTMLFormElement ||
                     threeDSFormRef.current?.querySelector('form') as HTMLFormElement;
        
        if (form && !form.hasAttribute('data-submitted')) {
          console.log('⚠️ Script çalışmadı, form manuel submit ediliyor...');
          form.setAttribute('data-submitted', 'true');
          form.submit();
        }
      }, 1000); // 1 saniye bekle, script'in çalışması için
    }
  }, [threeDSHtml]);

  // Form validasyonu
  const validateForm = (): boolean => {
    if (!formData.email || !formData.email.includes('@')) {
      setError('Geçerli bir e-posta adresi giriniz');
      return false;
    }
    if (!formData.name || !formData.surname) {
      setError('Ad ve soyad bilgilerini doldurunuz');
      return false;
    }
    if (!formData.address || !formData.city) {
      setError('Adres ve şehir bilgilerini doldurunuz');
      return false;
    }
    if (!formData.phone || formData.phone.length < 10) {
      setError('Geçerli bir telefon numarası giriniz');
      return false;
    }
    // TC kimlik numarası kontrolü (11 haneli)
    if (!formData.identityNumber || formData.identityNumber.length !== 11 || !/^\d{11}$/.test(formData.identityNumber)) {
      setError('Geçerli bir TC kimlik numarası giriniz (11 haneli)');
      return false;
    }
    // Kart bilgileri validasyonu
    if (!cardData.cardHolderName || cardData.cardHolderName.length < 3) {
      setError('Kart sahibi adını giriniz');
      return false;
    }
    if (!cardData.cardNumber || cardData.cardNumber.replace(/\s/g, '').length < 16) {
      setError('Geçerli bir kart numarası giriniz');
      return false;
    }
    if (!cardData.expireMonth || !cardData.expireYear) {
      setError('Kart son kullanma tarihini giriniz');
      return false;
    }
    if (!cardData.cvc || cardData.cvc.length < 3) {
      setError('CVC kodunu giriniz');
      return false;
    }
    return true;
  };

  // Ödeme başlatma
  const handlePayment = async () => {
    setError(null);
    
    if (!validateForm()) {
      return;
    }

    if (cartItems.length === 0) {
      setError('Sepetiniz boş');
      return;
    }

    setIsProcessing(true);

    try {
      // Benzersiz basket ID oluştur
      const basketId = `BASKET${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Ödeme isteği hazırla
      const paymentData = {
        price: total,
        currency: 'TRY',
        basketId: basketId,
        buyer: {
          id: `BUYER${Date.now()}`,
          name: formData.name,
          surname: formData.surname,
          email: formData.email,
          phone: formData.phone,
          gsmNumber: formData.phone,
          city: formData.city,
          country: 'Turkey',
          address: formData.address,
          zipCode: formData.postalCode || '34000',
          identityNumber: formData.identityNumber,
        },
        shippingAddress: {
          name: formData.name,
          surname: formData.surname,
          address: formData.apartment 
            ? `${formData.address}, ${formData.apartment}` 
            : formData.address,
          city: formData.city,
          country: 'Turkey',
          postalCode: formData.postalCode || '34000',
        },
        billingAddress: billingSameAsShipping ? undefined : {
          name: billingData.name || formData.name,
          surname: billingData.surname || formData.surname,
          address: billingData.apartment 
            ? `${billingData.address}, ${billingData.apartment}` 
            : billingData.address || formData.address,
          city: billingData.city || formData.city,
          country: 'Turkey',
          postalCode: billingData.postalCode || formData.postalCode || '34000',
        },
        basketItems: cartItems.map((item, index) => ({
          id: item.id,
          name: item.name,
          category1: 'Gelinlik',
          category2: 'Moda',
          price: item.price * item.quantity,
          quantity: item.quantity,
        })),
        paymentCard: {
          cardHolderName: cardData.cardHolderName,
          cardNumber: cardData.cardNumber.replace(/\s/g, ''),
          expireMonth: cardData.expireMonth,
          expireYear: cardData.expireYear,
          cvc: cardData.cvc,
        },
      };

      // Müşteri bilgilerini localStorage'a kaydet (sipariş oluşturma için)
      const customerData = {
        name: formData.name,
        surname: formData.surname,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        postalCode: formData.postalCode || '34000',
        country: 'Turkey',
        identityNumber: formData.identityNumber,
      };
      localStorage.setItem('checkout_customer_data', JSON.stringify(customerData));
      // Tutar kalemleri siparis kaydina gecmeli: eskiden kargo ucreti
      // iyzico'ya gonderiliyor ama siparise islenmiyordu.
      localStorage.setItem('checkout_totals', JSON.stringify({
        subtotal,
        shippingCost: shipping,
        discount,
        total,
        couponCode: appliedCoupon?.coupon?.code || null,
      }));
      localStorage.setItem('checkout_shipping_address', JSON.stringify(paymentData.shippingAddress));
      if (paymentData.billingAddress) {
        localStorage.setItem('checkout_billing_address', JSON.stringify(paymentData.billingAddress));
      }

      // API'ye istek gönder
      const response = await fetch('/api/payment/initialize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Ödeme başlatılamadı');
      }

      // 3D Secure HTML içeriği varsa decode et ve göster
      if (result.threeDSHtmlContent) {
        // Base64 kontrolü: Eğer 'PC9kb2N0' gibi başlıyorsa veya base64 karakterleri içeriyorsa decode et
        let decodedHtml = result.threeDSHtmlContent;
        
        // Base64 kontrolü: HTML tag'leri yoksa ve base64 karakterleri varsa decode et
        if (!decodedHtml.includes('<html') && !decodedHtml.includes('<form') && !decodedHtml.includes('<!DOCTYPE')) {
          try {
            decodedHtml = atob(decodedHtml);
            console.log('✅ HTML Base64 decode edildi');
          } catch (e) {
            console.log('⚠️ Base64 decode başarısız, düz HTML olarak kullanılıyor');
          }
        }
        
        setThreeDSHtml(decodedHtml);
        setIsProcessing(false); // Form gösterildiğinde loading'i kapat
      } else if (result.paymentPageUrl) {
        // Direkt ödeme sayfasına yönlendir
        window.location.href = result.paymentPageUrl;
      } else {
        throw new Error('Ödeme sayfası oluşturulamadı');
      }

    } catch (err: any) {
      console.error('Ödeme hatası:', err);
      
      // Local development'ta API çalışmaz
      if (window.location.hostname === 'localhost') {
        setError('Ödeme sistemi local development\'ta çalışmaz. Lütfen Vercel\'e deploy edip test edin.');
      } else {
        setError(err.message || 'Ödeme işlemi başlatılamadı. Lütfen tekrar deneyin.');
      }
      setIsProcessing(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <PageContainer>
        <Container>
          <BackButton onClick={() => navigate('/sepet')}>
            <ArrowLeft size={20} />
            Sepete Dön
          </BackButton>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <h2>Sepetiniz boş</h2>
            <p>Ödeme yapmak için sepetinize ürün ekleyin.</p>
          </div>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <BackButton onClick={() => navigate('/sepet')}>
          <ArrowLeft size={20} />
          Geri Dön
        </BackButton>

        <CheckoutGrid>
          <LeftPanel>
            {/* İletişim */}
            <Section>
              <SectionHeader>
                <h2>İletişim</h2>
                {user ? (
                  <span style={{ color: '#666', fontSize: '0.9rem' }}>
                    Merhaba, <strong>{formData.name || user.displayName || user.email}</strong>
                  </span>
                ) : (
                  <Link to="/login" style={{ color: '#333', textDecoration: 'none', fontSize: '0.9rem' }}>
                    Giriş yap
                  </Link>
                )}
              </SectionHeader>
              <FormGroup>
                <input
                  type="text"
                  placeholder="E-posta veya cep telefonu numarası"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </FormGroup>
              <Checkbox>
                <input type="checkbox" />
                <span>Haberler ve teklifler hakkında bana e-posta gönder</span>
              </Checkbox>
            </Section>

            {/* Teslimat */}
            <Section>
              <SectionHeader>
                <h2>Teslimat</h2>
              </SectionHeader>
              <FormGroup>
                <label>Ülke/Bölge</label>
                <select value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })}>
                  <option>Türkiye</option>
                </select>
              </FormGroup>
              <FormRow>
                <FormGroup>
                  <label>Ad (isteğe bağlı)</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </FormGroup>
                <FormGroup>
                  <label>Soyadı</label>
                  <input
                    type="text"
                    value={formData.surname}
                    onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                  />
                </FormGroup>
              </FormRow>
              <FormGroup>
                <label>Adres</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <label>Apartman, daire vb. (isteğe bağlı)</label>
                <input
                  type="text"
                  value={formData.apartment}
                  onChange={(e) => setFormData({ ...formData, apartment: e.target.value })}
                />
              </FormGroup>
              <FormRow>
                <FormGroup>
                  <label>Posta kodu (isteğe bağlı)</label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  />
                </FormGroup>
                <FormGroup>
                  <label>Şehir</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </FormGroup>
              </FormRow>
              <FormGroup>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  Telefon
                  <Info size={14} style={{ color: '#666' }} />
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <label>
                  TC Kimlik Numarası <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="11 haneli TC kimlik numarası"
                  maxLength={11}
                  value={formData.identityNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                    setFormData({ ...formData, identityNumber: value });
                  }}
                />
              </FormGroup>
              <Checkbox>
                <input type="checkbox" />
                <span>Bir sonraki işlem için bu bilgileri kaydet</span>
              </Checkbox>
            </Section>

            {/* Kargo Yöntemi */}
            <Section>
              <SectionHeader>
                <h2>Kargo yöntemi</h2>
              </SectionHeader>
              <ShippingMethod>
                <div className="method">
                  <input type="radio" name="shipping" defaultChecked />
                  <span>Standard</span>
                </div>
                <span className="price">ÜCRETSİZ</span>
              </ShippingMethod>
            </Section>

            {/* Ödeme */}
            <Section>
              <SectionHeader>
                <h2>Ödeme</h2>
              </SectionHeader>
              <SecurityText>
                <Lock size={16} />
                Tüm işlemler güvenli ve şifrelidir.
              </SecurityText>
              
              <PaymentMethod>
                <div className="method-header">
                  <input type="radio" name="payment" defaultChecked />
                  <span>Kredi / Banka Kartı</span>
                  <div className="logos">
                    <img src="/iyzico.png" alt="İyzico" style={{ height: '24px' }} />
                    <span>+5</span>
                  </div>
                </div>
                <div className="payment-info">
                  <FormGroup>
                    <label>Kart Sahibi Adı</label>
                    <input
                      type="text"
                      placeholder="Kart üzerindeki isim"
                      value={cardData.cardHolderName}
                      onChange={(e) => setCardData({ ...cardData, cardHolderName: e.target.value.toUpperCase() })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Kart Numarası</label>
                    <input
                      type="text"
                      placeholder="0000 0000 0000 0000"
                      maxLength={19}
                      value={cardData.cardNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\s/g, '').replace(/\D/g, '');
                        const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                        setCardData({ ...cardData, cardNumber: formatted });
                      }}
                    />
                  </FormGroup>
                  <FormRow>
                    <FormGroup>
                      <label>Son Kullanma Tarihi</label>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <input
                          type="text"
                          placeholder="AA"
                          maxLength={2}
                          value={cardData.expireMonth}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                            setCardData({ ...cardData, expireMonth: value });
                          }}
                        />
                        <span style={{ alignSelf: 'center' }}>/</span>
                        <input
                          type="text"
                          placeholder="YY"
                          maxLength={2}
                          value={cardData.expireYear}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                            setCardData({ ...cardData, expireYear: value });
                          }}
                        />
                      </div>
                    </FormGroup>
                    <FormGroup>
                      <label>CVC</label>
                      <input
                        type="text"
                        placeholder="000"
                        maxLength={3}
                        value={cardData.cvc}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 3);
                          setCardData({ ...cardData, cvc: value });
                        }}
                      />
                    </FormGroup>
                  </FormRow>
                </div>
              </PaymentMethod>
            </Section>

            {/* Fatura Adresi */}
            <Section>
              <SectionHeader>
                <h2>Fatura adresi</h2>
              </SectionHeader>
              <RadioGroup>
                <label>
                  <input 
                    type="radio" 
                    name="billing" 
                    checked={billingSameAsShipping}
                    onChange={() => setBillingSameAsShipping(true)}
                  />
                  <span>Kargo adresiyle aynı</span>
                </label>
                <label>
                  <input 
                    type="radio" 
                    name="billing"
                    checked={!billingSameAsShipping}
                    onChange={() => setBillingSameAsShipping(false)}
                  />
                  <span>Farklı bir fatura adresi kullan</span>
                </label>
              </RadioGroup>
              
              {!billingSameAsShipping && (
                <>
                  <FormGroup style={{ marginTop: '20px' }}>
                    <label>Ülke/Bölge</label>
                    <select value={billingData.country} onChange={(e) => setBillingData({ ...billingData, country: e.target.value })}>
                      <option>Türkiye</option>
                    </select>
                  </FormGroup>
                  <FormRow>
                    <FormGroup>
                      <label>Ad (isteğe bağlı)</label>
                      <input
                        type="text"
                        value={billingData.name}
                        onChange={(e) => setBillingData({ ...billingData, name: e.target.value })}
                      />
                    </FormGroup>
                    <FormGroup>
                      <label>Soyadı</label>
                      <input
                        type="text"
                        value={billingData.surname}
                        onChange={(e) => setBillingData({ ...billingData, surname: e.target.value })}
                      />
                    </FormGroup>
                  </FormRow>
                  <FormGroup>
                    <label>Adres</label>
                    <input
                      type="text"
                      value={billingData.address}
                      onChange={(e) => setBillingData({ ...billingData, address: e.target.value })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Apartman, daire vb. (isteğe bağlı)</label>
                    <input
                      type="text"
                      value={billingData.apartment}
                      onChange={(e) => setBillingData({ ...billingData, apartment: e.target.value })}
                    />
                  </FormGroup>
                  <FormRow>
                    <FormGroup>
                      <label>Posta kodu (isteğe bağlı)</label>
                      <input
                        type="text"
                        value={billingData.postalCode}
                        onChange={(e) => setBillingData({ ...billingData, postalCode: e.target.value })}
                      />
                    </FormGroup>
                    <FormGroup>
                      <label>Şehir</label>
                      <input
                        type="text"
                        value={billingData.city}
                        onChange={(e) => setBillingData({ ...billingData, city: e.target.value })}
                      />
                    </FormGroup>
                  </FormRow>
                  <FormGroup>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      Telefon
                      <Info size={14} style={{ color: '#666' }} />
                    </label>
                    <input
                      type="tel"
                      value={billingData.phone}
                      onChange={(e) => setBillingData({ ...billingData, phone: e.target.value })}
                    />
                  </FormGroup>
                </>
              )}
            </Section>

            {error && <ErrorMessage>{error}</ErrorMessage>}
            
            {threeDSHtml ? (
              <ThreeDSContainer id="iyzico-3ds-container">
                <div 
                  ref={threeDSFormRef} 
                  dangerouslySetInnerHTML={{ __html: threeDSHtml }} 
                />
              </ThreeDSContainer>
            ) : (
              <PayButton onClick={handlePayment} disabled={isProcessing}>
                {isProcessing ? (
                  <>
                    <Loader size={20} className="spinning" />
                    İşleniyor...
                  </>
                ) : (
                  'Şimdi öde'
                )}
              </PayButton>
            )}
          </LeftPanel>

          <RightPanel>
            <OrderSummary>
              <h3 style={{ fontSize: '1.2rem', marginBottom: '20px', fontWeight: 600 }}>Sipariş Özeti</h3>
              
              {cartItems.map(item => (
                <ProductItem key={item.id}>
                  <img src={item.image} alt={item.name} />
                  <div className="details">
                    <h4>{item.name}</h4>
                    <p>{item.size}</p>
                    <div className="price">₺{(item.price * item.quantity).toLocaleString()}</div>
                  </div>
                </ProductItem>
              ))}

              <DiscountCode>
                <input
                  type="text"
                  placeholder="İndirim kodu"
                  value={couponInput}
                  disabled={!!appliedCoupon}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyCoupon(); } }}
                />
                <button
                  type="button"
                  onClick={appliedCoupon ? handleRemoveCoupon : handleApplyCoupon}
                  disabled={checkingCoupon}
                >
                  {checkingCoupon ? '...' : appliedCoupon ? 'Kaldır' : 'Uygula'}
                </button>
              </DiscountCode>

              {couponError && (
                <div style={{ color: '#b3242f', fontSize: '0.82rem', marginTop: '6px' }}>
                  {couponError}
                </div>
              )}
              {appliedCoupon?.coupon && (
                <div style={{ color: '#1a7f3c', fontSize: '0.82rem', marginTop: '6px' }}>
                  {appliedCoupon.coupon.code} uygulandı —{' '}
                  {appliedCoupon.freeShipping
                    ? 'ücretsiz kargo'
                    : `%${appliedCoupon.coupon.value} indirim`}
                </div>
              )}

              <SummaryRow>
                <span>Alt toplam</span>
                <span>₺{subtotal.toLocaleString()}</span>
              </SummaryRow>
              {discount > 0 && (
                <SummaryRow>
                  <span>İndirim</span>
                  <span>-₺{discount.toLocaleString()}</span>
                </SummaryRow>
              )}
              <SummaryRow>
                <span>Kargo</span>
                <span>{shipping === 0 ? 'ÜCRETSİZ' : `₺${shipping.toLocaleString()}`}</span>
              </SummaryRow>
              <SummaryRow className="total">
                <span>Toplam</span>
                <span>TRY ₺{total.toLocaleString()}</span>
              </SummaryRow>
            </OrderSummary>
          </RightPanel>
        </CheckoutGrid>
      </Container>
    </PageContainer>
  );
};

export default CheckoutPage;

